/**
 * AdminStripeConfigPage
 * [UC-013] Configure Stripe integration settings
 */
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button, Input, Card } from '../components/ui';
import { api } from '../core/api-client';
import toast from 'react-hot-toast';

interface Currency {
  code: string;
  name: string;
}

interface StripeForm {
  publishableKey: string;
  secretKey: string;
  webhookSecret: string;
  webhookEndpoint: string;
  supportedCurrencies: string[];
}

export function AdminStripeConfigPage() {
  const [form, setForm] = useState<StripeForm>({
    publishableKey: '',
    secretKey: '',
    webhookSecret: '',
    webhookEndpoint: '',
    supportedCurrencies: []
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [configStatus, setConfigStatus] = useState<'verified' | 'unverified' | null>(null);
  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [warningMessage, setWarningMessage] = useState('');

  const navigate = useNavigate();
  const { t } = useTranslation();

  const availableCurrencies: Currency[] = [
    { code: 'usd', name: 'US Dollar' },
    { code: 'eur', name: 'Euro' },
    { code: 'gbp', name: 'British Pound' },
    { code: 'cad', name: 'Canadian Dollar' },
    { code: 'aud', name: 'Australian Dollar' },
    { code: 'jpy', name: 'Japanese Yen' }
  ];

  useEffect(() => {
    loadCurrentConfig();
  }, []);

  const loadCurrentConfig = async () => {
    try {
      setIsLoading(true);
      const config = await api.get('/payment-gateway-config') as any;

      setForm({
        publishableKey: config.publishableKey || '',
        secretKey: config.secretKey || '',
        webhookSecret: config.webhookSecret || '',
        webhookEndpoint: config.webhookEndpoint || '',
        supportedCurrencies: config.supportedCurrencies ? config.supportedCurrencies.split(',').map((c: string) => c.trim()) : []
      });

      setConfigStatus(config.verificationStatus === 'Verified' ? 'verified' : 'unverified');
    } catch (error) {
      console.error('Failed to load config:', error);
      // Config might not exist yet, use default form
    } finally {
      setIsLoading(false);
    }
  };

  const toggleCurrency = (currencyCode: string) => {
    setForm(prev => ({
      ...prev,
      supportedCurrencies: prev.supportedCurrencies.includes(currencyCode)
        ? prev.supportedCurrencies.filter(c => c !== currencyCode)
        : [...prev.supportedCurrencies, currencyCode]
    }));
  };

  const isFormValid = () => {
    return form.publishableKey.trim() !== '' &&
           form.secretKey.trim() !== '' &&
           form.webhookSecret.trim() !== '' &&
           form.webhookEndpoint.trim() !== '' &&
           form.supportedCurrencies.length > 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!isFormValid()) return;

    setIsSubmitting(true);
    setErrorMessage('');
    setSuccessMessage('');
    setWarningMessage('');

    try {
      const updateData = {
        publishableKey: form.publishableKey,
        secretKey: form.secretKey,
        webhookSecret: form.webhookSecret,
        webhookEndpoint: form.webhookEndpoint,
        supportedCurrencies: form.supportedCurrencies.join(', ')
      };

      const updatedConfig = await api.put('/payment-gateway-config', updateData) as any;

      if (updatedConfig.verificationStatus === 'Verified') {
        setSuccessMessage(t('adminStripeConfig.successVerified', 'Stripe configuration saved and verified successfully!'));
        setConfigStatus('verified');
        toast.success(t('adminStripeConfig.successVerified', 'Stripe configuration saved and verified successfully!'));
      } else {
        setWarningMessage(t('adminStripeConfig.warningUnverified', 'Configuration saved but Stripe test request failed. Please check your connection and try again later.'));
        setConfigStatus('unverified');
        toast.success(t('adminStripeConfig.savedUnverified', 'Configuration saved (unverified)'));
      }

    } catch (error: any) {
      console.error('Failed to save config:', error);

      if (error?.response?.status === 400) {
        setErrorMessage(t('adminStripeConfig.invalidKeys', 'Invalid API keys. Please check your publishable key and secret key format.'));
        setConfigStatus(null);
        toast.error(t('adminStripeConfig.invalidKeys', 'Invalid API keys'));
      } else {
        setErrorMessage(t('adminStripeConfig.saveFailed', 'Failed to save configuration. Please try again.'));
        setConfigStatus(null);
        toast.error(t('adminStripeConfig.saveFailed', 'Failed to save configuration'));
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    navigate('/admin/dashboard');
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="text-center">
          <div className="w-8 h-8 border-4 border-[var(--accent-primary)] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-[var(--text-muted)]">{t('common.loading', 'Loading...')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-full overflow-x-hidden">
      <div className="mb-5 sm:mb-6">
        <h1 className="text-2xl lg:text-3xl font-bold text-[var(--text-primary)]">
          {t('adminStripeConfig.title', 'Stripe Configuration')}
        </h1>
        <p className="mt-1.5 text-base sm:text-sm text-[var(--text-secondary)]">
          {t('adminStripeConfig.subtitle', 'Configure your Stripe integration settings to enable payment processing')}
        </p>
      </div>

      {/* Configuration Status */}
      {configStatus && (
        <div className={`mb-5 sm:mb-6 p-4 rounded-lg border ${
          configStatus === 'verified'
            ? 'bg-[var(--accent-primary)]/15 border-[var(--accent-primary)]/25'
            : 'bg-amber-600/15 border-amber-600/25'
        }`}>
          <div className="flex items-center gap-3">
            <div className={`h-2 w-2 rounded-full ${
              configStatus === 'verified' ? 'bg-[var(--accent-primary)]' : 'bg-amber-600'
            }`}></div>
            <span className={`text-sm font-medium ${
              configStatus === 'verified'
                ? 'text-[var(--accent-primary)]'
                : 'text-amber-700 dark:text-amber-500'
            }`}>
              {configStatus === 'verified'
                ? t('adminStripeConfig.integrationVerified', 'Stripe integration verified and active')
                : t('adminStripeConfig.integrationUnverified', 'Stripe integration saved but unverified')
              }
            </span>
          </div>
        </div>
      )}

      {/* Error Message */}
      {errorMessage && (
        <div className="mb-5 sm:mb-6 p-4 bg-red-600/15 border border-red-600/25 rounded-lg">
          <div className="flex items-start gap-3">
            <div className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5">
              <svg fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
            </div>
            <div>
              <h3 className="text-sm font-medium text-red-700 dark:text-red-500">
                {t('adminStripeConfig.configError', 'Configuration Error')}
              </h3>
              <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errorMessage}</p>
            </div>
          </div>
        </div>
      )}

      {/* Success Message */}
      {successMessage && (
        <div className="mb-5 sm:mb-6 p-4 bg-[var(--accent-primary)]/15 border border-[var(--accent-primary)]/25 rounded-lg">
          <div className="flex items-start gap-3">
            <div className="h-5 w-5 text-[var(--accent-primary)] flex-shrink-0 mt-0.5">
              <svg fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div>
              <h3 className="text-sm font-medium text-[var(--accent-primary)]">
                {t('adminStripeConfig.configSaved', 'Configuration Saved')}
              </h3>
              <p className="mt-1 text-sm text-[var(--accent-primary)]">{successMessage}</p>
            </div>
          </div>
        </div>
      )}

      {/* Warning Message */}
      {warningMessage && (
        <div className="mb-5 sm:mb-6 p-4 bg-amber-600/15 border border-amber-600/25 rounded-lg">
          <div className="flex items-start gap-3">
            <div className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5">
              <svg fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
            </div>
            <div>
              <h3 className="text-sm font-medium text-amber-700 dark:text-amber-500">
                {t('adminStripeConfig.configWarning', 'Configuration Warning')}
              </h3>
              <p className="mt-1 text-sm text-amber-600 dark:text-amber-400">{warningMessage}</p>
            </div>
          </div>
        </div>
      )}

      {/* Configuration Form */}
      <div className="bg-[var(--surface-base)] rounded-lg border border-[var(--border-subtle)] p-5 sm:p-6">
        <form onSubmit={handleSubmit}>
          <div className="space-y-6">
            {/* Publishable Key */}
            <div>
              <label className="block text-sm font-medium text-[var(--text-secondary)] mb-1.5">
                {t('adminStripeConfig.publishableKey', 'Publishable Key')}
              </label>
              <Input
                type="text"
                value={form.publishableKey}
                onChange={(e) => setForm(prev => ({ ...prev, publishableKey: e.target.value }))}
                placeholder="pk_test_..."
                disabled={isSubmitting}
                className="w-full px-4 py-3 sm:py-2.5"
              />
              <p className="mt-1.5 text-sm text-[var(--text-muted)]">
                {t('adminStripeConfig.publishableKeyHelp', 'Your Stripe publishable key (starts with pk_)')}
              </p>
            </div>

            {/* Secret Key */}
            <div>
              <label className="block text-sm font-medium text-[var(--text-secondary)] mb-1.5">
                {t('adminStripeConfig.secretKey', 'Secret Key')}
              </label>
              <Input
                type="password"
                value={form.secretKey}
                onChange={(e) => setForm(prev => ({ ...prev, secretKey: e.target.value }))}
                placeholder="sk_test_..."
                disabled={isSubmitting}
                className="w-full px-4 py-3 sm:py-2.5"
              />
              <p className="mt-1.5 text-sm text-[var(--text-muted)]">
                {t('adminStripeConfig.secretKeyHelp', 'Your Stripe secret key (starts with sk_)')}
              </p>
            </div>

            {/* Webhook Signing Secret */}
            <div>
              <label className="block text-sm font-medium text-[var(--text-secondary)] mb-1.5">
                {t('adminStripeConfig.webhookSecret', 'Webhook Signing Secret')}
              </label>
              <Input
                type="password"
                value={form.webhookSecret}
                onChange={(e) => setForm(prev => ({ ...prev, webhookSecret: e.target.value }))}
                placeholder="whsec_..."
                disabled={isSubmitting}
                className="w-full px-4 py-3 sm:py-2.5"
              />
              <p className="mt-1.5 text-sm text-[var(--text-muted)]">
                {t('adminStripeConfig.webhookSecretHelp', 'Stripe webhook signing secret (starts with whsec_) — used to verify incoming webhook events')}
              </p>
            </div>

            {/* Webhook Endpoint */}
            <div>
              <label className="block text-sm font-medium text-[var(--text-secondary)] mb-1.5">
                {t('adminStripeConfig.webhookEndpoint', 'Webhook Endpoint')}
              </label>
              <Input
                type="url"
                value={form.webhookEndpoint}
                onChange={(e) => setForm(prev => ({ ...prev, webhookEndpoint: e.target.value }))}
                placeholder="https://yourdomain.com/webhooks/stripe"
                disabled={isSubmitting}
                className="w-full px-4 py-3 sm:py-2.5"
              />
              <p className="mt-1.5 text-sm text-[var(--text-muted)]">
                {t('adminStripeConfig.webhookEndpointHelp', 'URL where Stripe will send webhook notifications')}
              </p>
            </div>

            {/* Supported Currencies */}
            <div>
              <label className="block text-sm font-medium text-[var(--text-secondary)] mb-1.5">
                {t('adminStripeConfig.supportedCurrencies', 'Supported Currencies')}
              </label>
              <div className="space-y-3">
                {availableCurrencies.map((currency) => (
                  <div
                    key={currency.code}
                    onClick={() => toggleCurrency(currency.code)}
                    className="flex items-center gap-3 p-3 rounded-lg cursor-pointer hover:bg-[var(--surface-raised)] active:scale-[0.99] transition-all"
                  >
                    <div className={`h-5 w-5 rounded flex items-center justify-center border-2 flex-shrink-0 transition-colors ${
                      form.supportedCurrencies.includes(currency.code)
                        ? 'bg-[var(--accent-primary)] border-[var(--accent-primary)]'
                        : 'bg-[var(--surface-raised)] border-[var(--border-default)]'
                    }`}>
                      {form.supportedCurrencies.includes(currency.code) && (
                        <svg className="h-3 w-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="3">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                        </svg>
                      )}
                    </div>
                    <div className="flex-1">
                      <div className="text-base sm:text-sm text-[var(--text-primary)] font-medium">
                        {currency.code.toUpperCase()}
                      </div>
                      <div className="text-sm text-[var(--text-muted)]">{currency.name}</div>
                    </div>
                  </div>
                ))}
              </div>
              <p className="mt-3 text-sm text-[var(--text-muted)]">
                {t('adminStripeConfig.supportedCurrenciesHelp', 'Select the currencies you want to accept payments in')}
              </p>
            </div>
          </div>

          {/* Form Actions */}
          <div className="mt-6 flex flex-col-reverse sm:flex-row sm:justify-end gap-3">
            <Button
              type="button"
              variant="secondary"
              onClick={handleCancel}
              disabled={isSubmitting}
              className="w-full sm:w-auto"
            >
              {t('common.cancel', 'Cancel')}
            </Button>
            <Button
              type="submit"
              disabled={isSubmitting || !isFormValid()}
              className="w-full sm:w-auto flex items-center justify-center gap-2"
            >
              {isSubmitting && (
                <div className="h-4 w-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
              )}
              {isSubmitting
                ? t('adminStripeConfig.testingConnection', 'Testing Connection...')
                : t('adminStripeConfig.saveConfiguration', 'Save Configuration')
              }
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default AdminStripeConfigPage;
